import React, {Component} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {casinoGamesStore} from 'app/store/global';
import CasinoRandomSlotCard from 'app/components/pages/casino/marketing-blocks/CasinoRandomSlotCard';
import {captureCasinoGameLaunchRow} from 'app/services/CasinoServices';
import {isDesktop} from 'app/utils';
import {
    animateReelSpin,
    computeSpinEndScrollLeft,
    reelCenteredIndexFromScrollLeft,
    reelScrollLeftToCenterIndex,
    reelSetWidthPx,
    reelValueInPeriod,
    repeatItemsForReel,
    roundReelValue,
    type ReelLayout
} from 'app/utils/reelCarousel';

interface CasinoRandomSlotProps {
    style?: React.CSSProperties;
}

interface CasinoRandomSlotState {
    centeredGameIndex: number;
    isSpinning: boolean;
    selectedIndex: number | null;
    viewportWidth: number | null;
}

const REPEAT_COUNT = 5;
const SPIN_DURATION_MS = 3000;
const CARD_WIDTH_PX = 108;
const CARD_GAP_PX = 8;

@observer
export default class CasinoRandomSlot extends Component<CasinoRandomSlotProps, CasinoRandomSlotState> {
    blockRef = React.createRef<HTMLDivElement>();

    trackRef = React.createRef<HTMLDivElement>();

    private justFinishedSpin = false;

    private scrollRafId: number | null = null;

    private initialScrollRafId: number | null = null;

    private selectedScrollRafId: number | null = null;

    private cancelSpin: (() => void) | null = null;

    private initialCarouselScrollDone = false;

    constructor(props: CasinoRandomSlotProps) {
        super(props);
        this.state = {
            centeredGameIndex: 0,
            isSpinning: false,
            selectedIndex: null,
            viewportWidth: null
        };
    }

    componentDidMount(): void {
        this.updateViewportWidth();
        window.addEventListener('resize', this.updateViewportWidth);
        this.applyInitialScrollAndSyncCentered();
    }

    componentDidUpdate(_prevProps: Readonly<CasinoRandomSlotProps>, prevState: CasinoRandomSlotState): void {
        if (this.syncViewportWidthOnUpdate()) {
            if (!this.initialCarouselScrollDone) {
                this.applyInitialScrollAndSyncCentered();
            }

            return;
        }

        this.syncScrollOnSelectedIndexChange(prevState);

        if (!this.initialCarouselScrollDone) {
            this.applyInitialScrollAndSyncCentered();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateViewportWidth);
        this.initialCarouselScrollDone = false;

        this.cancelAllPendingAnimationFrames();
    }

    private cancelAllPendingAnimationFrames(): void {
        if (this.scrollRafId !== null) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = null;
        }

        if (this.initialScrollRafId !== null) {
            cancelAnimationFrame(this.initialScrollRafId);
            this.initialScrollRafId = null;
        }

        if (this.selectedScrollRafId !== null) {
            cancelAnimationFrame(this.selectedScrollRafId);
            this.selectedScrollRafId = null;
        }

        this.cancelSpin?.();
        this.cancelSpin = null;
    }

    get singleSetWidth(): number {
        return reelSetWidthPx(casinoGamesStore.randomSlotGames.length, CARD_WIDTH_PX, CARD_GAP_PX);
    }

    private applyInitialScrollAndSyncCentered = (): void => {
        if (this.initialScrollRafId !== null) {
            cancelAnimationFrame(this.initialScrollRafId);
        }

        this.initialScrollRafId = requestAnimationFrame(() => {
            this.initialScrollRafId = null;

            if (!this.initialCarouselScrollDone) {
                this.applyInitialCarouselScrollOnce();
                this.syncCenteredFromTrack();
            }
        });
    };

    private applyInitialCarouselScrollOnce = (): void => {
        const track = this.trackRef.current;
        const games = casinoGamesStore.randomSlotGames;

        if (!track || games.length === 0 || track.clientWidth === 0) {
            return;
        }

        this.initialCarouselScrollDone = true;
        track.scrollLeft = roundReelValue(
            reelScrollLeftToCenterIndex(games.length, this.slotLayout(track.clientWidth))
        );
    };

    private setTrackScrollLeftAndSync = (scrollLeft: number): void => {
        const track = this.trackRef.current;

        if (!track) {
            return;
        }

        track.scrollLeft = scrollLeft;
        this.syncCenteredFromTrack();
    };

    handleCarouselScroll = (): void => {
        if (this.scrollRafId !== null) {
            return;
        }

        this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            const track = this.trackRef.current;

            if (!track || this.state.isSpinning) {
                return;
            }

            if (track.scrollLeft >= this.singleSetWidth) {
                this.setTrackScrollLeftAndSync(roundReelValue(track.scrollLeft - this.singleSetWidth));

                return;
            }

            this.syncCenteredFromTrack();
        });
    };

    private syncCenteredFromTrack = (): void => {
        const track = this.trackRef.current;

        if (this.state.isSpinning || !track) {
            return;
        }

        const next = this.computeCenteredGameIndex(track.scrollLeft);

        if (next !== this.state.centeredGameIndex) {
            this.setState({centeredGameIndex: next});
        }
    };

    syncViewportWidthOnUpdate = (): boolean => {
        if (this.state.viewportWidth !== null || !this.trackRef.current) {
            return false;
        }

        this.updateViewportWidth();
        return true;
    };

    syncScrollOnSelectedIndexChange = (prevState: CasinoRandomSlotState): void => {
        const {isSpinning, selectedIndex} = this.state;

        if (selectedIndex === null || prevState.selectedIndex === selectedIndex || isSpinning || !this.trackRef.current) {
            return;
        }

        if (this.justFinishedSpin) {
            this.justFinishedSpin = false;
            return;
        }

        if (this.selectedScrollRafId !== null) {
            cancelAnimationFrame(this.selectedScrollRafId);
        }

        this.selectedScrollRafId = requestAnimationFrame(() => {
            this.selectedScrollRafId = null;
            this.scrollSelectedToCenter();
        });
    };

    getViewportWidth = (track: HTMLDivElement): number => this.state.viewportWidth ?? track.clientWidth;

    private slotLayout = (viewportWidth: number): ReelLayout => ({
        cardWidth: CARD_WIDTH_PX,
        gap: CARD_GAP_PX,
        viewportWidth
    });

    updateViewportWidth = (): void => {
        const track = this.trackRef.current;

        if (!track) {
            return;
        }

        const measuredViewportWidth = track.clientWidth;

        if (measuredViewportWidth === this.state.viewportWidth) {
            return;
        }

        this.setState({viewportWidth: measuredViewportWidth}, () => {
            this.syncCenteredFromTrack();
        });
    };

    runSpinAnimation = (winnerIndex: number): void => {
        const track = this.trackRef.current;
        const games = casinoGamesStore.randomSlotGames;
        const {singleSetWidth} = this;

        if (!track || games.length === 0) {
            return;
        }

        const viewportWidth = this.getViewportWidth(track);
        const canonicalStart = roundReelValue(reelValueInPeriod(track.scrollLeft, singleSetWidth));

        if (canonicalStart !== track.scrollLeft) {
            track.scrollLeft = canonicalStart;
        }

        const targetScroll = reelScrollLeftToCenterIndex(
            winnerIndex + games.length,
            this.slotLayout(viewportWidth)
        );
        const endLeft = roundReelValue(
            computeSpinEndScrollLeft(canonicalStart, singleSetWidth, targetScroll)
        );

        this.scheduleSpinRafLoop({canonicalStart, endLeft, track, winnerIndex});
    };

    private scheduleSpinRafLoop(params: {
        canonicalStart: number
        endLeft: number
        track: HTMLDivElement
        winnerIndex: number
    }): void {
        const {canonicalStart, endLeft, track, winnerIndex} = params;

        this.cancelSpin?.();
        this.cancelSpin = animateReelSpin({
            apply: (value: number) => {
                track.scrollLeft = value;
            },
            durationMs: SPIN_DURATION_MS,
            from: canonicalStart,
            onDone: () => {
                this.cancelSpin = null;
                this.justFinishedSpin = true;
                const centeredGameIndex = this.computeCenteredGameIndex(track.scrollLeft);

                this.setState({
                    centeredGameIndex,
                    isSpinning: false,
                    selectedIndex: winnerIndex
                });
            },
            to: endLeft
        });
    }

    scrollSelectedToCenter = (): void => {
        const track = this.trackRef.current;
        const {selectedIndex} = this.state;

        if (!track || selectedIndex === null) {
            return;
        }

        const viewportWidth = this.getViewportWidth(track);
        const targetIndex = selectedIndex + casinoGamesStore.randomSlotGames.length;

        this.setTrackScrollLeftAndSync(
            reelScrollLeftToCenterIndex(targetIndex, this.slotLayout(viewportWidth))
        );
    };

    handleSpin = (): void => {
        const {isSpinning} = this.state;
        const games = casinoGamesStore.randomSlotGames;

        if (games.length === 0 || isSpinning) {
            return;
        }

        const winnerIndex = Math.floor(Math.random() * games.length);

        this.setState({isSpinning: true, selectedIndex: null}, () => {
            this.runSpinAnimation(winnerIndex);
        });
    };

    setIframeGridPlacement = (): void => {
        if (!isDesktop()) {
            return;
        }

        captureCasinoGameLaunchRow(this.blockRef.current);
    };

    computeCenteredGameIndex(scrollLeft: number): number {
        const games = casinoGamesStore.randomSlotGames;
        const repeatedCount = games.length * REPEAT_COUNT;

        if (repeatedCount === 0) {
            return 0;
        }

        const track = this.trackRef.current;

        if (!track) {
            return 0;
        }

        const viewportWidth = this.getViewportWidth(track);

        return reelCenteredIndexFromScrollLeft(scrollLeft, this.slotLayout(viewportWidth), repeatedCount);
    }

    render(): React.ReactNode {
        const {centeredGameIndex, isSpinning} = this.state;
        const {style} = this.props;
        const games = casinoGamesStore.randomSlotGames;
        const loading = casinoGamesStore.loadingNewGames && games.length === 0;
        const repeatedGames = repeatItemsForReel(games, REPEAT_COUNT);

        if (loading) {
            return (
                <div
                    className="casino-random-slot casino-random-slot--loading marketing-block"
                    style={style}
                >
                    <div className="casino-random-slot__loading-bar" />
                </div>
            );
        }

        if (games.length === 0) {
            return (
                <div className="casino-random-slot casino-random-slot--empty marketing-block" style={style}>
                    <div className="casino-random-slot__empty-inner">
                        <SpriteIcon name="#svg-dice" className="svg-default casino-random-slot__icon" />
                        <p className="casino-random-slot__empty-text">{I18n.t('empty_games1')}</p>
                    </div>
                </div>
            );
        }

        return (
            <div ref={this.blockRef} className="casino-random-slot marketing-block" style={style}>
                <div className="casino-random-slot__panel">
                    <div className="casino-random-slot__panel-top">
                        <SpriteIcon name="#svg-dice" className="svg-default casino-random-slot__icon" />
                        <h4 className="casino-random-slot__title">{I18n.t('random_game')}</h4>
                    </div>
                    <button
                        type="button"
                        className="casino-random-slot__spin-btn"
                        onClick={this.handleSpin}
                        disabled={isSpinning}
                    >
                        {I18n.t('spin')}
                    </button>
                </div>
                <div className="casino-random-slot__carousel-wrap">
                    <div
                        ref={this.trackRef}
                        className="casino-random-slot__carousel"
                        onScroll={this.handleCarouselScroll}
                    >
                        {repeatedGames.map((game, gameIndex) => <CasinoRandomSlotCard
                            key={`random-slot-${gameIndex}-${game.id}`}
                            game={game}
                            isSelected={gameIndex === centeredGameIndex}
                            setIframeGridPlacement={this.setIframeGridPlacement}
                        />)}
                    </div>
                    <div className="casino-random-slot__center-frame">
                        <SpriteIcon name="#svg-triangle-full-soft" className="svg-default casino-random-slot__center-frame-icon top" />
                        <SpriteIcon name="#svg-triangle-full-soft" className="svg-default casino-random-slot__center-frame-icon bottom" />
                    </div>
                </div>
            </div>
        );
    }
}
